import { Component, computed, inject, OnInit, Signal, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, IRowNode, RowSelectedEvent } from 'ag-grid-community';
import { pull, sortBy, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { linkedQueryParam, paramToNumber } from 'ngxtension/linked-query-param';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { convertDateToTimezone } from '../../../date.utils';
import { tryGetErrorMessage } from '../../../error.utils';
import { caseMeetsMinimumIncidents, defaultTableDefOptions } from '../../../helpers';
import { customSearchForIdAndName } from '../../../search.utils';
import { CaseTrackingService } from '../../../service/case-tracking.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { ReportsService } from '../../../service/reports.service';
import {
  Branch,
  CaseIncidentTableEntry,
  CaseStatus,
  CaseTrackingLocation,
  CaseWithIncidents,
  GetCasesByLocationsRequest,
  LocationResponse,
  Pagination,
  ShareCasesRequest,
  ShareLogEntry,
} from '../../../types';
import { LocationGroup } from '../../../types/location-group-response';
import { CellTheftReportVideoComponent } from '../../pages/theft-report/cell-theft-report-video/cell-theft-report-video.component';
import { CellCaseTrackingActionsComponent } from './cell-case-tracking-actions/cell-case-tracking-actions.component';
import { CellCaseTrackingGroupComponent } from './cell-case-tracking-group/cell-case-tracking-group.component';
import { CellCaseTrackingPhotoComponent } from './cell-case-tracking-photo/cell-case-tracking-photo.component';

type LocationResponseWithGroup = LocationResponse & { _group?: string };

@Component({
  selector: 'app-pan-case-tracking-portal',
  templateUrl: './case-tracking-portal.component.html',
  styleUrl: './case-tracking-portal.component.scss',
  standalone: false,
})
export class CaseTrackingPortalComponent implements OnInit {
  private router = inject(Router);
  private notifyService = inject(NotifyService);
  private posthogService = inject(PosthogService);
  private locationService = inject(LocationService);
  private caseTrackingService = inject(CaseTrackingService);
  private reportService = inject(ReportsService);

  readonly pageBranchIds = linkedQueryParam<number[]>('branch_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  readonly pageLocationIds = linkedQueryParam<number[]>('location_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  readonly pageLocationGroupIds = linkedQueryParam<string[]>('location_group_ids', {
    parse: (value) => (value ? value.split(',') : []),
    stringify: (value) => value.join(','),
  });

  readonly pageDaysBack = linkedQueryParam<number | null>('since_days', {
    parse: paramToNumber(),
  });

  readonly pageIncidentDateFrom = linkedQueryParam<Date | undefined>('incident_date_from', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  readonly pageIncidentDateTo = linkedQueryParam<Date | undefined>('incident_date_to', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  public minIncidents = signal<number>(0);

  public caseGrid = viewChild<AgGridAngular>('caseGrid');
  public findingsModal = viewChild<PanModalComponent>('findingsModal');
  public findings = signal<string>('');

  public loadingLocations = signal<boolean>(false);
  public loadingCases = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);

  public defaultBranchIds = signal<number[]>([]);
  public defaultLocationGroupIds = signal<string[]>([]);

  public selectedBranches = signal<Branch[]>([]);
  public selectedLocationIds = signal<number[]>([]);
  public selectedLocationGroups = signal<LocationGroup[]>([]);
  public customSearchFn = customSearchForIdAndName;
  public branchesAndLocationsMap = signal<Map<number, LocationResponseWithGroup[]>>(new Map<number, LocationResponseWithGroup[]>());
  public allLocations = signal<LocationResponseWithGroup[]>([]);
  public availableLocations = computed<LocationResponseWithGroup[]>(() => {
    const map = this.branchesAndLocationsMap();
    const branchIds = this.selectedBranches();
    if (!branchIds || branchIds?.length === 0) return this.allLocations();

    return branchIds.reduce<LocationResponseWithGroup[]>((acc, branch) => {
      const locations = map.get(branch.id);
      if (locations) acc.push(...locations);
      return acc;
    }, []);
  });

  public showMergeCases = computed(() => this.cases().length > 0);
  public showShareCase = computed<boolean>(
    () =>
      this.posthogService.isReady() &&
      this.posthogService.isFeatureEnabled('case_tracking_share', true) &&
      this.selectedLocationIds() &&
      this.selectedLocationIds().length > 0 &&
      this.cases().length > 0,
  );
  public disableShareCases = computed(() => uniq(this.selectedCases().map((c) => c.location_id)).length > 1);

  public disableActions = computed(() => this.loadingLocations() || this.loadingCases());
  public sortedLocations = computed(() => sortBy(this.availableLocations(), [(loc) => -loc.recent_incidents_count, (loc) => loc.name]));

  public canRefresh = computed(
    () => this.selectedBranches().length > 0 || this.selectedLocationIds().length > 0 || this.selectedLocationGroups().length > 0,
  );

  public cases = signal<CaseWithIncidents[]>([]);
  public shareHistories = signal<Map<string, ShareLogEntry[]>>(new Map());
  public isMerging = signal<boolean>(false);
  public incidentDateFrom = signal<Date | undefined>(undefined);
  public incidentDateTo = signal<Date | undefined>(undefined);

  public canSelectCases = computed(
    () => this.selectedLocationIds().length <= 1 && this.selectedBranches().length <= 1 && this.showShareCase(),
  );

  public selectedCases = signal<CaseIncidentTableEntry[]>([]);

  public isSharing = signal<boolean>(false);
  public canShareCases = computed(() => Object.keys(this.selectedCases()).length > 0);
  public canMergeCases = computed(() => Object.keys(this.selectedCases()).length >= 2);

  public isResolvingSelected = signal<boolean>(false);
  public canResolveSelectedCases = computed(() => this.selectedCases().every((c) => c.status !== CaseStatus.Resolved));
  public canUnresolveSelectedCases = computed(() => this.selectedCases().every((c) => c.status === CaseStatus.Resolved));

  public numNewCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.New && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );
  public numViewedCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.Viewed && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );
  public numClientCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.ViewedByClient && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );
  public numResolvedCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.Resolved && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );

  public ungroupedCases: Signal<CaseIncidentTableEntry[]> = computed(() => {
    const minIncidents = this.minIncidents();
    const allCases = this.cases();

    return allCases.flatMap((c) => {
      if (!caseMeetsMinimumIncidents(c, minIncidents)) return [];

      return c.incidents.flatMap((i) => ({
        ...i,
        ...c,
        case_id: c.id,
        case_num: c.case_index,
        instance_num: i.instance_num,
        case_index: `${c.case_index} @ ${c.location_name}`,
        first_incident_date: new Date(c.first_incident_date),
        most_recent_incident_date: new Date(c.most_recent_incident_date),
        incident_date: convertDateToTimezone(i.incident_date, c.location_timezone),
        date_shared: new Date(i.date_shared),
      }));
    });
  });

  public incidentIdsForSelectedCases = computed(() => this.selectedCases().flatMap((c) => c.incidents.map((i) => i.review_outcome_id)));

  public readonly columnDefs: ColDef[] = [
    {
      field: 'case_index',
      headerName: 'Case #',
      rowGroup: true,
      hide: true,
    },
    {
      field: 'branch_name',
      headerName: 'Branch',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipValueGetter: (params) => params.data?.external_branch_id ?? '',
    },
    {
      field: 'location_name',
      headerName: 'Location',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipValueGetter: (params) => params.data?.external_location_id ?? '',
    },
    {
      field: 'incident_date',
      headerName: 'Incident Date',
      cellDataType: 'date',
      width: 100,
      sortable: true,
    },
    {
      field: 'date_shared',
      headerName: 'Date Shared',
      cellDataType: 'date',
      width: 100,
      sortable: true,
    },
    {
      field: 'photo',
      headerName: 'Photo',
      suppressColumnsToolPanel: true,
      sortable: false,
      width: 64,
      cellRenderer: CellCaseTrackingPhotoComponent,
    },
    {
      headerName: 'Video',
      suppressColumnsToolPanel: true,
      sortable: false,
      width: 64,
      cellRenderer: CellTheftReportVideoComponent,
    },
    {
      field: 'actions',
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 200,
      maxWidth: 300,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellCaseTrackingActionsComponent,
      cellRendererParams: {
        findingsCallback: (item: CaseIncidentTableEntry) => {
          this.findings.set(item.summary);
          this.findingsModal().show();
        },
      },
    },
  ];

  public readonly tableDef: GridOptions<CaseIncidentTableEntry> = {
    ...defaultTableDefOptions(),
    tooltipShowMode: 'standard',
    rowHeight: 56,
    groupDisplayType: 'groupRows',
    groupRowRenderer: 'agGroupCellRenderer',
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
    onRowSelected: (event: RowSelectedEvent<CaseIncidentTableEntry>) => {
      const children = event.node.allLeafChildren;
      const currentSelection = this.selectedCases();
      const newSelection = currentSelection.slice();
      children.forEach((child) => {
        if (currentSelection.includes(child.data)) pull(newSelection, child.data);
        else newSelection.push(child.data);
      });

      this.selectedCases.set(newSelection);
    },
    rowSelection: {
      mode: 'multiRow',
      checkboxLocation: 'autoGroupColumn',
      groupSelects: 'self',
      isRowSelectable: (rowNode) => rowNode.group,
      hideDisabledCheckboxes: true,
    },
    selectionColumnDef: {
      maxWidth: 55,
      headerClass: 'pl-[43px]',
      suppressHeaderMenuButton: true,
    },
    groupRowRendererParams: {
      suppressCount: true,
      innerRenderer: CellCaseTrackingGroupComponent,
      innerRendererParams: {
        hasShareHistory: (caseId: string) => (this.shareHistories().get(caseId)?.length ?? 0) > 0,
      },
    },
  };

  ngOnInit() {
    this.loadingLocations.set(true);
    this.incidentDateFrom.set(DateTime.now().minus({ days: 30 }).toJSDate());
    this.incidentDateTo.set(new Date());

    setTimeout(() => {
      this.loadFromURLParams();
    }, 0);

    this.locationService
      .getAllLocations({ count_incidents: true, has_plan: true, vertical: 'MicroMarket' })
      .pipe(finalize(() => this.loadingLocations.set(false)))
      .subscribe(
        (locations) => {
          const map = new Map<number, LocationResponseWithGroup[]>();
          const allLocations: LocationResponseWithGroup[] = locations.items;
          allLocations.forEach((location) => {
            const branchId = location.branch_id;
            if (!map.has(branchId)) {
              map.set(branchId, []);
            }
            map.get(branchId)?.push(location);
          });
          this.branchesAndLocationsMap.set(map);

          allLocations.forEach((loc) => (loc._group = loc.has_recent_incidents ? 'Has Recent Incidents' : 'Other Locations'));
          this.allLocations.set(sortBy(allLocations, (l) => l.name.trim()));
        },
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  private loadFromURLParams() {
    const branchIds = this.pageBranchIds();
    const locationIds = this.pageLocationIds();
    const locationGroupIds = this.pageLocationGroupIds();
    const daysBack = this.pageDaysBack();
    const incidentFrom = this.pageIncidentDateFrom();
    const incidentTo = this.pageIncidentDateTo();

    this.defaultBranchIds.set(branchIds);
    this.selectedLocationIds.set(locationIds);
    this.defaultLocationGroupIds.set(locationGroupIds);

    if (incidentFrom) this.incidentDateFrom.set(incidentFrom);
    else if (daysBack) this.incidentDateFrom.set(DateTime.now().minus({ days: +daysBack }).toJSDate());

    if (incidentTo) this.incidentDateTo.set(incidentTo);
    if (locationIds || branchIds || daysBack || incidentFrom || incidentTo) this.refresh();
  }

  locationGroupBy(loc: CaseTrackingLocation) {
    return loc._group;
  }

  onDefaultSelected() {
    if (this.availableLocations().length === 0) return;

    setTimeout(() => {
      if (this.selectedLocationIds().length === 0) {
        this.selectedLocationIds.set([this.availableLocations()[0].id]);
      }

      this.onSelection();
    });
  }

  onBranchSelection() {
    this.selectedLocationIds.set([]);
    this.onSelection();
  }

  onSelection() {
    this.error.set(undefined);
    this.refresh();
  }

  refresh() {
    this.updateDeepLink();
    this.unselectCases();

    if (!this.canRefresh()) {
      return;
    }

    this.loadingCases.set(true);
    const request: GetCasesByLocationsRequest = {
      location_ids: this.selectedLocationIds(),
      branch_ids: this.selectedBranches().map((b) => b.id),
      location_group_ids: this.selectedLocationGroups().map((g) => g.id),
      incident_date_from: this.incidentDateFrom(),
      incident_date_to: this.incidentDateTo(),
      mode: 'default',
    };

    this.caseTrackingService
      .getCasesByLocations(request)
      .pipe(finalize(() => this.loadingCases.set(false)))
      .subscribe({
        next: (response: Pagination<CaseWithIncidents>) => {
          this.cases.set(response.items ?? []);
          this.shareHistories.set(new Map());
          const ids = (response.items ?? []).map((c) => c.id);
          if (ids.length > 0) {
            this.caseTrackingService.getShareHistories(request).subscribe(
              (histories: Record<string, ShareLogEntry[]>) => {
                const map = new Map<string, ShareLogEntry[]>();
                const keys = Object.keys(histories);
                for (const id of keys) map.set(id, histories[id]);
                this.shareHistories.set(map);
              },
              () => this.shareHistories.set(new Map()),
            );
          }
        },
        error: (err) => this.error.set(tryGetErrorMessage(err)),
      });
  }

  merge() {
    this.isMerging.set(true);
    const review_ids = this.selectedCases().map((checkCase) => checkCase.review_id);

    this.caseTrackingService
      .mergeCases({ review_ids })
      .pipe(finalize(() => this.isMerging.set(false)))
      .subscribe(
        () => this.notifyService.notify('Successfully merged cases'),
        (err) => this.error.set(tryGetErrorMessage(err)),
        () => {
          this.unselectCases();
          this.refresh();
        },
      );
  }

  share(shareData: Partial<ShareCasesRequest>) {
    this.isSharing.set(true);
    this.caseTrackingService
      .shareCases({
        ...shareData,
        case_ids: this.selectedCases().map((c) => c.id),
      })
      .pipe(finalize(() => this.isSharing.set(false)))
      .subscribe(
        () => this.notifyService.notify('Successfully shared case report'),
        () => this.notifyService.notify('Failed to share report'),
      );
  }

  unselectCases() {
    this.caseGrid()?.api?.deselectAll();

    setTimeout(() => {
      this.selectedCases.set([]);
    }, 0);
  }

  selectNewCases() {
    this.caseGrid()?.api?.forEachNode((node: IRowNode<CaseIncidentTableEntry>) => {
      if (!node.group) return;
      if (node.allLeafChildren[0].data.status !== 'New') return;

      node.setSelected(true);
    });
  }

  updateDeepLink() {
    this.pageLocationIds.set(this.selectedLocationIds());
    this.pageBranchIds.set(this.selectedBranches().map((b) => b.id));
    this.pageLocationGroupIds.set(this.selectedLocationGroups().map((g) => g.id));
    this.pageIncidentDateFrom.set(this.incidentDateFrom());
    this.pageIncidentDateTo.set(this.incidentDateTo());
  }

  ensureMinIncidents() {
    this.minIncidents.set(Math.max(0, this.minIncidents()));
  }

  expandAll() {
    this.caseGrid()?.api?.expandAll();
  }

  collapseAll() {
    this.caseGrid()?.api?.collapseAll();
  }

  resolveSelected() {
    this.isResolvingSelected.set(true);

    forkJoin(this.selectedCases().map((c) => this.caseTrackingService.resolveCase(c.id)))
      .pipe(finalize(() => this.isResolvingSelected.set(false)))
      .subscribe(() => {
        this.unselectCases();
        this.refresh();
        this.notifyService.notify('Successfully resolved selected cases');
      });
  }

  unresolveSelected() {
    this.isResolvingSelected.set(true);

    forkJoin(this.selectedCases().map((c) => this.caseTrackingService.unresolveCase(c.id)))
      .pipe(finalize(() => this.isResolvingSelected.set(false)))
      .subscribe(() => {
        this.unselectCases();
        this.refresh();
        this.notifyService.notify('Successfully unresolved selected cases');
      });
  }

  reportNameValidate(name: string) {
    const checkedName = name.trim();
    if (checkedName.length < 2) return 'Report name too short.';
    if (checkedName.length > 25) return 'Report name too long. Max 25 characters.';

    return null;
  }

  createReport($event: string) {
    const reportName = $event.trim();
    if (!reportName) {
      this.notifyService.notify('Report name is required');
      return;
    }

    this.reportService
      .createReport({
        review_outcome_ids: this.incidentIdsForSelectedCases(),
        title: reportName,
      })
      .subscribe((data) => {
        this.notifyService.notify('Report created successfully');

        this.router.navigate(['/shared', 'report', data.id]);
      });
  }
}
